import { FC, useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAsync } from 'react-use'
import { gql, useQuery } from '@apollo/client'
import { useLocalStorage } from '@rehooks/local-storage'
import { writeStorage } from '@rehooks/local-storage'
import { ConnectedUser, emitChange } from '@ucc/admin-global-state'
import { BILLING_ROUTE, GTC_BILLING_ROUTE, Roles } from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import AccountContext from 'components/contexts/AccountContext'
import { AppErrorContext } from 'components/contexts/AppErrorContext'
import config from 'config'
import { useManagedAccounts, useUserIsBillingAdminOnly } from 'hooks'
import { Account } from 'types/Account'
import { ProductsSettings } from 'types/ProductSettings'
import { fetchAccount, fetchIAm, fetchLicensesDetails, fetchPbxId, LicensesDetails, mixpanelEventNames } from 'utils'
import fetchAccountSettings from 'utils/fetchAccountSettings'
import useUserHasOnlyBillingAdminRoles from '../../hooks/useUserHasOnlyBillingAdminRoles'

export const FETCH_ACCOUNTS_QUERY = gql`
  query {
    accounts {
      key
      orgId
      name
    }
  }
`

const AccountProvider: FC = ({ children }) => {
  const { loading, data: { accounts = [] } = {} } = useQuery(FETCH_ACCOUNTS_QUERY)
  const history = useHistory()
  const userIsBillingAdminOnly = useUserIsBillingAdminOnly()
  const userIsGtcBillingAdminOnly = useUserHasOnlyBillingAdminRoles()

  const pathAccountKey = history.location.pathname.split('/')[1]
  const getPathAccountKey = useCallback(() => history.location.pathname.split('/')[1], [history.location.pathname])

  const isAccountKeyValid = (key: string) => {
    const accountKeyLong = Number(key || '')
    return !isNaN(accountKeyLong)
  }
  const [selectedAccountKey, setSelectedAccountKey] = useState<string | null>(
    isAccountKeyValid(pathAccountKey) ? pathAccountKey : null,
  )
  const [iAmInfos] = useLocalStorage<ConnectedUser>(config.I_AM_TOKEN_KEY)
  const { setError: setAppError } = useContext(AppErrorContext)
  const { loading: managedAccountsLoading, managedAccounts } = useManagedAccounts()
  const [storedSelectedPbxId] = useLocalStorage<string | null>(config.SELECTED_PBX_ID)
  const [storedSelectedAccountKey] = useLocalStorage<string | null>(config.SELECTED_ACCOUNT_KEY)
  const [isAccountLoaded, setIsAccountLoaded] = useState(false)
  const [isAccountContextSettled, setIsAccountContextSettled] = useState(false)
  const [licensesDetails, setLicensesDetails] = useState<LicensesDetails>()
  const [productsSettings, setProductsSettings] = useState<ProductsSettings | null>(null)
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null)

  const setIsSettled = useCallback((isSettled: boolean) => {
    setIsAccountContextSettled(isSettled)
  }, [])

  const getAccountDetails = async (accountKey?: string) => {
    let foundAccount

    const pathAccountKey = accountKey || getPathAccountKey()

    // 1- match account with key from path
    if (pathAccountKey) {
      foundAccount = accounts.find(({ key }) => key === pathAccountKey)

      if (foundAccount && !foundAccount.partnerAccountKey) {
        const account = await fetchAccount(pathAccountKey)
        if (account && !account.errors) {
          foundAccount = {
            ...foundAccount,
            unifiedAdmin: account.unifiedAdmin,
            toggleUnifiedAdmin: account.toggleUnifiedAdmin,
            goToAdminTime: account.goToAdminTime,
          }
          if (account.partnerAccountKey) {
            foundAccount = {
              ...foundAccount,
              partnerAccountKey: account.partnerAccountKey,
            }
          }
        }
      }

      // 2- system user can access any accounts with key from path
      if (!foundAccount && iAmInfos?.systemRoles?.includes(Roles.SystemUser)) {
        const orgId = await fetchPbxId(pathAccountKey)
        const account = await fetchAccount(pathAccountKey)

        if (account && !account.errors) {
          foundAccount = {
            goToAdminTime: account?.goToAdminTime,
            key: pathAccountKey,
            name: account?.name || '',
            orgId: orgId || '',
            parent: account?.parent,
            parentKey: account?.parentKey,
            partnerAccountKey: account?.partnerAccountKey,
            toggleUnifiedAdmin: account?.toggleUnifiedAdmin,
            unifiedAdmin: account?.unifiedAdmin,
          }
        }
      }
    }
    // 3- partner user with managed accounts
    if (!foundAccount && iAmInfos?.products?.includes('PARTNER')) {
      let key = pathAccountKey
      if (!key && !accounts.length && managedAccounts.length) {
        key = managedAccounts[0].key
      }
      const iAm = await fetchIAm(key)

      if (iAm.managedAccount) {
        const orgId = await fetchPbxId(key)
        const account = await fetchAccount(key)

        foundAccount = {
          goToAdminTime: account?.goToAdminTime,
          isPartner: true,
          key,
          managedAdminRoles: iAm.managedAccount.adminRoles,
          name: account?.name || '',
          orgId: orgId || '',
          partnerAccountKey: account?.partnerAccountKey,
          unifiedAdmin: account?.unifiedAdmin,
          toggleUnifiedAdmin: account?.toggleUnifiedAdmin,
        }
      }
    }
    // 4- partner user with child accounts
    if (!foundAccount && iAmInfos?.accounts?.some((account) => account.parent)) {
      const key = pathAccountKey
      const iAm = await fetchIAm('', key)

      if (iAm.childAccount) {
        const orgId = await fetchPbxId(key)
        const account = await fetchAccount(key)

        foundAccount = {
          isPartner: true,
          key,
          managedAdminRoles: iAm.childAccount.adminRoles,
          billingRoles: iAm.childAccount.billingRoles,
          name: iAm.childAccount.name || '',
          orgId: orgId || '',
          partnerAccountKey: account?.partnerAccountKey,
          unifiedAdmin: account?.unifiedAdmin,
          toggleUnifiedAdmin: account?.toggleUnifiedAdmin,
        }
      }
    }

    // 5- used the stored account/pbx
    if (!foundAccount && !pathAccountKey && (storedSelectedAccountKey || storedSelectedPbxId)) {
      foundAccount = accounts.find(({ key, orgId }) =>
        storedSelectedAccountKey ? key === storedSelectedAccountKey : orgId === storedSelectedPbxId,
      )
    }
    // 6- use first account if there was no account in path
    // could maybe be the main account set in their iAm infos?
    if (!foundAccount && !pathAccountKey) {
      foundAccount = accounts[0]
    }
    // 7- user doesn't have access, account doesn't exist or isn't linked to a pbx, redirect to error page
    if (
      !foundAccount ||
      !isAccountKeyValid(foundAccount.key) ||
      (foundAccount && foundAccount.isPartner && !foundAccount.managedAdminRoles && !foundAccount.billingRoles)
    ) {
      setAppError('account-access')
    }

    setCurrentAccount(foundAccount)
    setSelectedAccountKey(foundAccount.key)

    setIsAccountLoaded(true)
    setIsSettled && setIsSettled(true)
  }

  const setCurrentSelectedAccountKey = async (key: string) => {
    await getAccountDetails(key)
  }
  useAsync(async () => {
    if (!loading && !managedAccountsLoading) {
      await getAccountDetails()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, managedAccountsLoading])

  useEffect(() => {
    if (selectedAccountKey && isAccountKeyValid(selectedAccountKey) && getPathAccountKey() !== selectedAccountKey) {
      if (userIsBillingAdminOnly(selectedAccountKey)) {
        history.push(`/${selectedAccountKey}/${BILLING_ROUTE}/subscriptions`)
      } else if (userIsGtcBillingAdminOnly(selectedAccountKey)) {
        history.push(`/${selectedAccountKey}/${GTC_BILLING_ROUTE}/invoices`)
      } else {
        history.push(`/${selectedAccountKey}/${config.DEFAULT_APP}`)
      }
    }
    writeStorage(config.SELECTED_ACCOUNT_KEY, { key: selectedAccountKey })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountKey])

  useAsync(async () => {
    if (currentAccount?.key) {
      const licensesDetails = await fetchLicensesDetails(currentAccount.key)
      setLicensesDetails && setLicensesDetails({ ...licensesDetails, isLoaded: true })

      mixpanel.track(mixpanelEventNames.ACCOUNT, {
        Action: `Fetched licenses`,
        products: licensesDetails?.products,
      })
      const settings = await fetchAccountSettings(licensesDetails, currentAccount)

      if (settings.GOTORESOLVE === undefined) {
        settings.GOTORESOLVE = { resolveDataResidency: 'no-connection' }
      } else if (settings.GOTORESOLVE?.resolveDataResidency === undefined) {
        settings.GOTORESOLVE.resolveDataResidency = 'not-set'
      } else if (typeof settings.GOTORESOLVE?.resolveDataResidency === 'string') {
        // This workaround is needed because results may be pascal or lower case but only lowercase values are expected by the unified-admin-core.
        // Synapse-Token-Service creates these values in pascal case despite being defined as lower case in the initial RFC
        // https://confluence.ops.expertcity.com/display/CES/RFC-101+EU+Data+Residency
        settings.GOTORESOLVE.resolveDataResidency = settings.GOTORESOLVE.resolveDataResidency.toLowerCase()
      }
      setProductsSettings && setProductsSettings(settings || {})
      emitChange((prev) => ({ ...prev, resolveDataResidency: settings.GOTORESOLVE?.resolveDataResidency }))
    } else {
      setLicensesDetails &&
        setLicensesDetails({
          products: [],
          entitlements: {},
          isLoaded: false,
        })
    }
  }, [currentAccount?.key])

  useEffect(() => {
    const unlisten = history.listen(async (location, action) => {
      const keyFromPath = location.pathname.split('/')[1]
      if (action === 'POP' && keyFromPath !== selectedAccountKey) {
        await getAccountDetails(keyFromPath)
      }
    })
    return () => {
      unlisten()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountKey])

  return (
    <AccountContext.Provider
      value={{
        accountKey: selectedAccountKey,
        currentAccount,
        orgId: currentAccount?.orgId,
        setSelectedAccountKey: setCurrentSelectedAccountKey,
        accounts,
        setCurrentAccount,
        managedAdminRoles: currentAccount?.managedAdminRoles,
        isPartner: currentAccount?.isPartner ?? false,
        isSettled: isAccountContextSettled,
        setIsSettled,
        isAccountLoaded,
        partnerAccountKey: currentAccount?.partnerAccountKey,
        unifiedAdmin: currentAccount?.unifiedAdmin ?? false,
        toggleUnifiedAdmin: currentAccount?.toggleUnifiedAdmin ?? false,
        goToAdminTime: currentAccount?.goToAdminTime,
        productsSettings,
        setProductsSettings,
        licensesDetails,
        setLicensesDetails,
        billingRoles: currentAccount?.billingRoles,
        accountName: currentAccount?.name,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}
export default AccountProvider
